import { useState, useEffect } from "react";
import axios from "axios";
import { Grid3x3, Plus, Minus, Trash2, ShoppingCart, X, CreditCard, Banknote, Edit2, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TablesPage = () => {
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [extras, setExtras] = useState([]); // Extra ingredients list
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
  const [selectedItemsForPayment, setSelectedItemsForPayment] = useState([]); // [{index: 0, quantity: 2}, ...]
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("nakit");
  const [mealCardType, setMealCardType] = useState("sodexo");
  const [discount, setDiscount] = useState(0); // Discount amount
  const [showPrintInstructions, setShowPrintInstructions] = useState(false); // Show Ctrl+P instructions
  const [showTransferModal, setShowTransferModal] = useState(false); // Masa taşıma modalı
  const [targetTableId, setTargetTableId] = useState(null); // Hedef masa ID
  const [currentTime, setCurrentTime] = useState(Date.now()); // Süre hesaplama için
  
  // Ingredient customization modal states
  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [removedIngredients, setRemovedIngredients] = useState([]);
  const [addedIngredients, setAddedIngredients] = useState([]);
  const [customQuantity, setCustomQuantity] = useState(1);
  const [specialNote, setSpecialNote] = useState("");
  const [selectedSize, setSelectedSize] = useState(null); // For size selection
  const [isExtrasOpen, setIsExtrasOpen] = useState(false); // Ekstra malzeme accordion
  const [isIngredientsOpen, setIsIngredientsOpen] = useState(false); // Çıkarılacak malzeme accordion
  const [editingExistingItem, setEditingExistingItem] = useState(null); // Mevcut sipariş düzenleme

  useEffect(() => {
    loadTables();
    loadCategories();
    loadProducts();
    loadExtras();
    const interval = setInterval(loadTables, 5000);
    return () => clearInterval(interval);
  }, []);

  // Süre sayacı için her saniye güncelle
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000); // Her 1 saniyede bir güncelle
    
    return () => clearInterval(interval);
  }, []);

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`);
      const tablesData = response.data;
      
      // Dolu masalar için sipariş bilgilerini yükle (süre hesaplama için)
      const tablesWithOrders = await Promise.all(
        tablesData.map(async (table) => {
          if (table.status === "dolu" && table.current_order_id) {
            try {
              const orderResponse = await axios.get(`${API}/orders/${table.current_order_id}`);
              return {
                ...table,
                order_items: orderResponse.data.items,
                order_total: orderResponse.data.total,
                order_created_at: orderResponse.data.created_at
              };
            } catch (error) {
              console.error(`Masa ${table.number} siparişi yüklenemedi:`, error);
              return table;
            }
          }
          return table;
        })
      );
      
      setTables(tablesWithOrders);
      
      // NOT: selectedTable'ı burada GÜNCELLEMİYORUZ
      // Çünkü kısmi ödemede zaten doğru değer set ediliyor
      // loadTables sadece masa listesini güncellemeli
    } catch (error) {
      console.error("Masalar yüklenirken hata:", error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      // İlk kategoriyi otomatik seç
      if (response.data.length > 0 && !selectedCategory) {
        setSelectedCategory(response.data[0].id);
      }
    } catch (error) {
      console.error("Kategoriler yüklenirken hata:", error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Ürünler yüklenirken hata:", error);
    }
  };

  const loadExtras = async () => {
    try {
      const response = await axios.get(`${API}/extras`);
      setExtras(response.data);
    } catch (error) {
      console.error("Ekstra malzemeler yüklenirken hata:", error);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "boş": return "bg-green-500";
      case "dolu": return "bg-red-500";
      case "rezerve": return "bg-yellow-500";
      default: return "bg-gray-500";
    }
  };

  // Masanın açık olduğu süreyi hesapla
  const getTableDuration = (table) => {
    if (table.status !== "dolu" || !table.order_created_at) {
      return null;
    }
    
    // Sipariş oluşturma zamanından şimdiye kadar geçen süre
    const orderTime = new Date(table.order_created_at);
    const now = new Date(currentTime);
    const diffMs = now - orderTime;
    
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours}s ${remainingMinutes}dk`;
    }
    return `${minutes}dk`;
  };

  const handleTableClick = async (table) => {
    // Farklı masa seçiliyorsa cart'ı temizle
    if (!selectedTable || selectedTable.id !== table.id) {
      setCart([]);
      setDiscount(0);
    }
    
    setSelectedTable(table);
    
    // Dolu masada mevcut siparişleri yükle (ama cart'a EKLEME!)
    if (table.status === "dolu" && table.current_order_id) {
      try {
        const response = await axios.get(`${API}/orders/${table.current_order_id}`);
        const order = response.data;
        
        // Masaya order_items ekle (kısmi ödeme ve gösterim için)
        const updatedTable = {
          ...table,
          order_items: order.items,
          order_total: order.total
        };
        setSelectedTable(updatedTable);
      } catch (error) {
        console.error("Mevcut sipariş yüklenemedi:", error);
      }
    }
    
    setShowOrderModal(true);
  };

  const openIngredientModal = (product, cartIndex = null) => {
    setSelectedProduct(product);
    setEditingCartIndex(cartIndex);
    setIsExtrasOpen(false); // Accordion kapalı başlasın
    setIsIngredientsOpen(false); // Çıkarılacak malzeme accordion kapalı
    
    // If editing existing cart item, load its customizations
    if (cartIndex !== null) {
      const cartItem = cart[cartIndex];
      setRemovedIngredients(cartItem.removed_ingredients || []);
      setAddedIngredients(cartItem.added_ingredients || []);
      setCustomQuantity(cartItem.quantity);
      setSpecialNote(cartItem.special_note || "");
      setSelectedSize(cartItem.size || (product.sizes && product.sizes.length > 0 ? product.sizes[0].name : null));
    } else {
      setRemovedIngredients([]);
      setAddedIngredients([]);
      setCustomQuantity(1);
      setSpecialNote("");
      // Set default size if product has sizes
      setSelectedSize(product.sizes && product.sizes.length > 0 ? product.sizes[0].name : null);
    }
    
    setShowIngredientModal(true);
  };

  const closeIngredientModal = () => {
    setShowIngredientModal(false);
    setSelectedProduct(null);
    setEditingCartIndex(null);
    setEditingExistingItem(null);
    setRemovedIngredients([]);
    setAddedIngredients([]);
    setCustomQuantity(1);
    setSpecialNote("");
    setSelectedSize(null);
  };

  // Mevcut siparişi düzenleme modalını aç
  const openEditExistingItem = (item, index) => {
    // Ürünü bul
    const product = products.find(p => p.id === item.product_id);
    if (!product) {
      alert("Ürün bulunamadı!");
      return;
    }
    
    setSelectedProduct(product);
    setEditingExistingItem({ item, index });
    setEditingCartIndex(null);
    setRemovedIngredients(item.removed_ingredients || []);
    setAddedIngredients(item.added_ingredients || []);
    setCustomQuantity(item.quantity);
    setSpecialNote(item.special_note || "");
    setSelectedSize(item.size || (product.sizes && product.sizes.length > 0 ? product.sizes[0].name : null));
    setIsExtrasOpen(false);
    setIsIngredientsOpen(false);
    setShowIngredientModal(true);
  };

  // Mevcut siparişi kaydet
  const saveEditedExistingItem = async () => {
    if (!editingExistingItem || !selectedProduct) return;
    
    try {
      const { index } = editingExistingItem;
      
      // Fiyat hesapla
      let basePrice = selectedProduct.price;
      if (selectedSize && selectedProduct.sizes) {
        const sizeObj = selectedProduct.sizes.find(s => s.name === selectedSize);
        if (sizeObj) basePrice = sizeObj.price;
      }
      
      // Ekstra malzeme fiyatı
      const extraPrice = addedIngredients.reduce((sum, ing) => {
        const extra = extras.find(e => e.name === ing);
        return sum + (extra ? extra.price : 0);
      }, 0);
      
      const finalPrice = basePrice + extraPrice;
      
      // Güncellenmiş ürün
      const updatedItem = {
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        price: finalPrice,
        quantity: customQuantity,
        size: selectedSize,
        removed_ingredients: removedIngredients,
        added_ingredients: addedIngredients,
        special_note: specialNote
      };
      
      // order_items'ı güncelle
      const updatedItems = [...selectedTable.order_items];
      updatedItems[index] = updatedItem;
      
      const newTotal = updatedItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      
      // Backend'e gönder
      await axios.put(`${API}/orders/${selectedTable.current_order_id}`, {
        items: updatedItems,
        total: newTotal
      });
      
      // State güncelle
      setSelectedTable({
        ...selectedTable,
        order_items: updatedItems,
        order_total: newTotal
      });
      
      closeIngredientModal();
      loadTables();
    } catch (error) {
      console.error("Ürün güncelleme hatası:", error);
      alert("Ürün güncellenemedi: " + (error.response?.data?.detail || error.message));
    }
  };

  const toggleRemovedIngredient = (ingredient) => {
    if (removedIngredients.includes(ingredient)) {
      setRemovedIngredients(removedIngredients.filter(i => i !== ingredient));
    } else {
      setRemovedIngredients([...removedIngredients, ingredient]);
    }
  };

  const toggleAddedIngredient = (ingredient) => {
    if (addedIngredients.includes(ingredient)) {
      setAddedIngredients(addedIngredients.filter(i => i !== ingredient));
    } else {
      setAddedIngredients([...addedIngredients, ingredient]);
    }
  };

  const addToCartWithCustomization = () => {
    // Get price based on size or base price
    let finalPrice = selectedProduct.price;
    if (selectedSize && selectedProduct.sizes && selectedProduct.sizes.length > 0) {
      const sizeInfo = selectedProduct.sizes.find(s => s.name === selectedSize);
      if (sizeInfo) finalPrice = sizeInfo.price;
    }
    
    // Add extra ingredients prices
    let extraPrice = 0;
    const addedIngredientsDetails = [];
    addedIngredients.forEach(ingredientName => {
      if (selectedProduct.extra_ids && selectedProduct.extra_ids.length > 0) {
        selectedProduct.extra_ids.forEach(extraId => {
          const extraItem = extras.find(e => e.id === extraId);
          if (extraItem && extraItem.name === ingredientName) {
            let price = extraItem.price || 0;
            
            // Check for size-based pricing
            if (extraItem.size_prices && selectedSize) {
              const sizePrice = extraItem.size_prices.find(sp => sp.size === selectedSize.toLowerCase());
              price = sizePrice ? sizePrice.price : price;
            }
            
            extraPrice += price;
            addedIngredientsDetails.push({
              name: ingredientName,
              price: price
            });
          }
        });
      }
    });
    
    const cartItem = {
      product_id: selectedProduct.id,
      product_name: selectedProduct.name,
      quantity: customQuantity,
      price: finalPrice + extraPrice, // Add extra prices
      size: selectedSize || undefined,
      removed_ingredients: removedIngredients,
      added_ingredients: addedIngredients,
      added_ingredients_details: addedIngredientsDetails, // NEW: with prices
      special_note: specialNote || undefined
    };

    if (editingCartIndex !== null) {
      // Update existing cart item
      const newCart = [...cart];
      newCart[editingCartIndex] = cartItem;
      setCart(newCart);
    } else {
      // Add new item to cart
      setCart([...cart, cartItem]);
    }

    closeIngredientModal();
  };

  const addToCart = (product) => {
    // Open ingredient modal instead of directly adding to cart
    openIngredientModal(product);
  };

  const updateQuantity = (index, delta) => {
    // Index bazlı güncelleme - her ürün varyasyonu ayrı
    setCart(cart.map((item, idx) => {
      if (idx === index) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) {
          return null; // Adet 0 olursa ürünü sil
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean)); // null olanları temizle
  };

  const removeFromCart = (productId, index = null) => {
    if (index !== null) {
      // Remove specific item by index
      setCart(cart.filter((_, idx) => idx !== index));
    } else {
      // Remove by product_id (legacy support)
      setCart(cart.filter(item => item.product_id !== productId));
    }
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const addOrderToTable = async () => {
    if (cart.length === 0) {
      return;
    }

    try {
      // Clean cart items for backend (remove added_ingredients_details)
      const cleanedItems = cart.map(item => {
        const { added_ingredients_details, ...cleanItem } = item;
        return cleanItem;
      });

      // Masada zaten sipariş var mı ve items var mı kontrol et
      if (selectedTable.current_order_id && selectedTable.order_items && selectedTable.order_items.length > 0) {
        // MEVCUT SİPARİŞİ GÜNCELLE (yeni sipariş oluşturma!)
        const existingOrder = selectedTable.order_items || [];
        const updatedItems = [...existingOrder, ...cleanedItems];
        const newTotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        await axios.put(`${API}/orders/${selectedTable.current_order_id}`, {
          items: updatedItems,
          total: newTotal
        });
        
      } else {
        // YENİ SİPARİŞ OLUŞTUR
        const orderData = {
          table_id: selectedTable.id,
          table_number: selectedTable.number,
          customer_name: "Misafir",
          items: cleanedItems,
          total: calculateTotal(),
          order_type: "masa",
          payment_status: "ödenmedi"
        };

        const response = await axios.post(`${API}/orders`, orderData);
        
        // Masayı güncelle - order_id'yi set et
        await axios.put(`${API}/tables/${selectedTable.id}`, {
          status: "dolu",
          current_order_id: response.data.id
        });
      }
      
      // Modal'ı kapat
      setShowOrderModal(false);
      setCart([]);
      setSelectedTable(null);
      
      loadTables();
    } catch (error) {
      console.error("❌ [TablesPage] Sipariş ekleme hatası:", error);
      console.error("❌ [TablesPage] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      alert(`Sipariş eklenemedi!\n\nHata: ${error.response?.data?.detail || error.message}`);
    }
  };

  const addOrderAndPrint = async () => {
    if (cart.length === 0) {
      return;
    }

    try {
      // Clean cart items for backend (remove added_ingredients_details)
      const cleanedItems = cart.map(item => {
        const { added_ingredients_details, ...cleanItem } = item;
        return cleanItem;
      });

      let orderResponse;

      // Masada zaten sipariş var mı ve items var mı kontrol et
      if (selectedTable.current_order_id && selectedTable.order_items && selectedTable.order_items.length > 0) {
        // MEVCUT SİPARİŞİ GÜNCELLE
        const existingOrder = selectedTable.order_items || [];
        const updatedItems = [...existingOrder, ...cleanedItems];
        const newTotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        await axios.put(`${API}/orders/${selectedTable.current_order_id}`, {
          items: updatedItems,
          total: newTotal
        });
        
        // Güncellenmiş siparişi al
        orderResponse = await axios.get(`${API}/orders/${selectedTable.current_order_id}`);
        
      } else {
        // YENİ SİPARİŞ OLUŞTUR
        const orderData = {
          table_id: selectedTable.id,
          table_number: selectedTable.number,
          customer_name: "Misafir",
          items: cleanedItems,
          total: calculateTotal(),
          order_type: "masa",
          payment_status: "ödenmedi"
        };

        orderResponse = await axios.post(`${API}/orders`, orderData);
        
        // Masayı güncelle
        await axios.put(`${API}/tables/${selectedTable.id}`, {
          status: "dolu",
          current_order_id: orderResponse.data.id
        });
      }
      
      // Yazdırma modalını aç
      const orderWithDetails = {
        ...orderResponse.data,
        items: cart.map((cartItem, idx) => ({
          ...(orderResponse.data.items[orderResponse.data.items.length - cart.length + idx] || {}),
          added_ingredients_details: cartItem.added_ingredients_details
        })),
        table_info: `${selectedTable.number}`
      };
      
      setLastOrder(orderWithDetails);
      setShowPrintModal(true);
      
      loadTables();
    } catch (error) {
      console.error("❌ [TablesPage] Sipariş ekleme hatası:", error);
      console.error("❌ [TablesPage] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      alert(`Sipariş eklenemedi!\n\nHata: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handlePrintComplete = () => {
    setShowPrintModal(false);
    setShowOrderModal(false);
    setCart([]);
    setSelectedTable(null);
    loadTables();
  };

  const initiatePayment = async () => {
    // Masada mevcut sipariş olmalı
    if (!selectedTable?.order_items || selectedTable.order_items.length === 0) {
      alert("Masada sipariş yok!");
      return;
    }
    
    // Cart'ta kaydedilmemiş ürün var mı kontrol et
    if (cart.length > 0) {
      const userConfirm = window.confirm(
        "Sepette kaydedilmemiş ürünler var!\n\n" +
        "Bu ürünleri otomatik olarak siparişe ekleyip ödemeye devam edilsin mi?\n\n" +
        "✅ Evet: Ürünler kaydedilecek ve ödeme açılacak\n" +
        "❌ İptal: Ödeme iptal edilecek"
      );
      
      if (!userConfirm) {
        return;
      }
      
      // Siparişi kaydet
      try {
        await addOrderToTable();
        await loadTables();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Güncel masa bilgilerini al
        const updatedTables = await axios.get(`${API}/tables`);
        const updatedTable = updatedTables.data.find(t => t.id === selectedTable.id);
        
        if (updatedTable && updatedTable.current_order_id) {
          const orderResponse = await axios.get(`${API}/orders/${updatedTable.current_order_id}`);
          const updatedSelectedTable = {
            ...updatedTable,
            order_items: orderResponse.data.items,
            order_total: orderResponse.data.total,
          };
          setSelectedTable(updatedSelectedTable);
        }
        
      } catch (error) {
        console.error("Sipariş kaydetme hatası:", error);
        alert("Sipariş kaydedilemedi: " + (error.response?.data?.detail || error.message));
        return;
      }
    }
    
    setShowPaymentModal(true);
  };

  const completeOrder = async () => {
    try {
      const finalPaymentMethod = paymentMethod === "yemek-karti" 
        ? `Yemek Kartı (${mealCardType.charAt(0).toUpperCase() + mealCardType.slice(1)})`
        : paymentMethod === "kredi-karti" 
        ? "Kredi Kartı" 
        : "Nakit";

      // Masada mevcut sipariş olmalı
      if (!selectedTable?.order_items || selectedTable.order_items.length === 0) {
        alert("Masada sipariş yok!");
        return;
      }

      // Mevcut siparişlerin toplam tutarını hesapla
      const orderTotal = selectedTable.order_items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const finalTotal = orderTotal - discount;

      // Backend'deki current_order_id siparişini güncelle
      if (selectedTable.current_order_id) {
        await axios.put(`${API}/orders/${selectedTable.current_order_id}`, {
          payment_status: "ödendi",
          payment_method: finalPaymentMethod,
          discount: discount,
          total: finalTotal
        });
      }

      // Masayı boşalt
      await axios.put(`${API}/tables/${selectedTable.id}`, {
        status: "boş",
        current_order_id: null
      });
      
      setShowPaymentModal(false);
      setShowOrderModal(false);
      setCart([]);
      setSelectedTable(null);
      setPaymentMethod("nakit");
      setMealCardType("sodexo");
      setDiscount(0);
      
      loadTables();
    } catch (error) {
      console.error("❌ [TablesPage] Ödeme hatası:", error);
      console.error("❌ [TablesPage] Ödeme error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      alert(`Ödeme işlenirken hata oluştu!\n\nHata: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Kısmi ödeme fonksiyonları
  const openPartialPayment = async () => {
    console.log("Kısmi ödeme açılıyor, selectedTable:", selectedTable);
    console.log("Order items:", selectedTable?.order_items);
    console.log("Cart:", cart);
    
    if (!selectedTable?.order_items || selectedTable.order_items.length === 0) {
      alert("Bu masada aktif sipariş yok veya siparişler yüklenemedi");
      return;
    }
    
    // Cart'ta kaydedilmemiş ürün var mı kontrol et
    if (cart.length > 0) {
      const userConfirm = window.confirm(
        "Sepette kaydedilmemiş ürünler var!\n\n" +
        "Bu ürünleri otomatik olarak siparişe ekleyip kısmi ödemeye devam edilsin mi?\n\n" +
        "✅ Evet: Ürünler kaydedilecek ve kısmi ödeme açılacak\n" +
        "❌ İptal: Kısmi ödeme iptal edilecek"
      );
      
      if (!userConfirm) {
        return; // Kullanıcı iptal etti
      }
      
      // Siparişi kaydet
      try {
        await addOrderToTable();
        
        // Masaları yeniden yükle
        await loadTables();
        
        // 1 saniye bekle - masa bilgileri güncellensin
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Güncel masa bilgilerini al
        const updatedTables = await axios.get(`${API}/tables`);
        const updatedTable = updatedTables.data.find(t => t.id === selectedTable.id);
        
        if (updatedTable && updatedTable.current_order_id) {
          const orderResponse = await axios.get(`${API}/orders/${updatedTable.current_order_id}`);
          const updatedSelectedTable = {
            ...updatedTable,
            order_items: orderResponse.data.items,
            order_total: orderResponse.data.total,
          };
          setSelectedTable(updatedSelectedTable);
          
          console.log("✅ Sipariş kaydedildi, güncel order_items:", orderResponse.data.items);
        }
        
      } catch (error) {
        console.error("Sipariş kaydetme hatası:", error);
        alert("Sipariş kaydedilemedi: " + (error.response?.data?.detail || error.message));
        return;
      }
    }
    
    setSelectedItemsForPayment([]);
    setShowPartialPaymentModal(true);
  };

  // Ürünleri grupla - BASİT VERSİYON (her ürün kendi index'iyle)
  const groupOrderItems = (items) => {
    if (!items || items.length === 0) return [];
    
    // Her ürünü kendi grubu olarak döndür - GRUPLAMA YOK
    return items.map((item, idx) => ({
      ...item,
      total_quantity: item.quantity,
      indices: [idx],
      originalIndex: idx,
      items: [item]
    }));
  };

  // Ürün seçimi - BASİT VERSİYON
  const toggleItemSelection = (itemIndex, quantity) => {
    console.log(`🎯 [toggleItemSelection] itemIndex: ${itemIndex}, quantity: ${quantity}`);
    
    const existingItem = selectedItemsForPayment.find(item => item.index === itemIndex);
    
    if (existingItem) {
      // Zaten seçili, kaldır
      const newSelection = selectedItemsForPayment.filter(i => i.index !== itemIndex);
      console.log(`🎯 Seçim kaldırıldı. Kalan seçimler:`, newSelection);
      setSelectedItemsForPayment(newSelection);
    } else {
      // Yeni seçim ekle
      const newSelection = [...selectedItemsForPayment, { index: itemIndex, quantity: quantity }];
      console.log(`🎯 Yeni seçim eklendi. Tüm seçimler:`, newSelection);
      setSelectedItemsForPayment(newSelection);
    }
  };

  const updatePartialQuantity = (mainIndex, newQuantity, maxQuantity) => {
    if (newQuantity < 1 || newQuantity > maxQuantity) return;
    
    setSelectedItemsForPayment(
      selectedItemsForPayment.map(item => 
        item.index === mainIndex ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const calculatePartialTotal = () => {
    if (!selectedTable?.order_items) return 0;
    
    return selectedItemsForPayment.reduce((sum, selection) => {
      const item = selectedTable.order_items[selection.index];
      if (item) {
        return sum + (item.price * selection.quantity);
      }
      return sum;
    }, 0);
  };

  const completePartialPayment = async () => {
    if (selectedItemsForPayment.length === 0) {
      alert("Lütfen ödeme yapılacak ürünleri seçin");
      return;
    }

    try {
      const finalPaymentMethod = paymentMethod === "yemek-karti" 
        ? `Yemek Kartı (${mealCardType.charAt(0).toUpperCase() + mealCardType.slice(1)})`
        : paymentMethod === "kredi-karti" 
        ? "Kredi Kartı" 
        : "Nakit";

      console.log("🔥 ========== KISMİ ÖDEME BAŞLIYOR ==========");
      console.log("🔥 Seçilen ürünler (selectedItemsForPayment):", selectedItemsForPayment);
      console.log("🔥 Tüm ürünler (order_items):", selectedTable.order_items);

      // Seçili index'leri Set olarak tut
      const selectedIndices = new Set(selectedItemsForPayment.map(s => s.index));
      console.log("🔥 Seçili index'ler:", Array.from(selectedIndices));

      // Ödenen ve kalan ürünleri ayır - BASİT MANTIK
      const paidItems = [];
      const remainingItems = [];

      selectedTable.order_items.forEach((item, index) => {
        const selection = selectedItemsForPayment.find(s => s.index === index);
        
        if (selection) {
          // ✅ BU ÜRÜN SEÇİLMİŞ - ÖDE
          const paidQty = selection.quantity;
          
          paidItems.push({
            product_id: item.product_id,
            product_name: item.product_name,
            price: item.price,
            quantity: paidQty,
            size: item.size || null,
            removed_ingredients: item.removed_ingredients || [],
            added_ingredients: item.added_ingredients || [],
            special_note: item.special_note || ""
          });
          console.log(`✅ INDEX ${index}: "${item.product_name}" x ${paidQty} → ÖDENEN`);

          // Kalan miktar varsa
          const remainingQty = item.quantity - paidQty;
          if (remainingQty > 0) {
            remainingItems.push({
              product_id: item.product_id,
              product_name: item.product_name,
              price: item.price,
              quantity: remainingQty,
              size: item.size || null,
              removed_ingredients: item.removed_ingredients || [],
              added_ingredients: item.added_ingredients || [],
              special_note: item.special_note || ""
            });
            console.log(`   ↳ Kalan: ${remainingQty} adet`);
          }
        } else {
          // ❌ BU ÜRÜN SEÇİLMEMİŞ - KALACAK
          remainingItems.push({
            product_id: item.product_id,
            product_name: item.product_name,
            price: item.price,
            quantity: item.quantity,
            size: item.size || null,
            removed_ingredients: item.removed_ingredients || [],
            added_ingredients: item.added_ingredients || [],
            special_note: item.special_note || ""
          });
          console.log(`❌ INDEX ${index}: "${item.product_name}" x ${item.quantity} → KALACAK`);
        }
      });

      console.log("🔥 ===== SONUÇ =====");
      console.log("🔥 Ödenen ürünler:", paidItems.map(i => `${i.product_name} x ${i.quantity}`));
      console.log("🔥 Kalan ürünler:", remainingItems.map(i => `${i.product_name} x ${i.quantity}`));

      // Ödenen toplam
      const paidTotal = paidItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      console.log(`💰 Ödenen Toplam: ${paidTotal} TL`);

      if (paidItems.length === 0) {
        alert("Ödeme yapılacak ürün bulunamadı!");
        return;
      }

      // YENİ SİPARİŞ OLUŞTUR (ödenen ürünler için)
      // NOT: table_id GÖNDERMİYORUZ çünkü backend masayı güncelliyor ve current_order_id'yi bu siparişe çekiyor
      // Bu kısmi ödeme olduğu için masada kalan sipariş hala aktif olmalı
      const paidOrderResponse = await axios.post(`${API}/orders`, {
        table_number: selectedTable.number,
        customer_name: "Misafir",
        items: paidItems,
        total: paidTotal,
        order_type: "masa",
        payment_status: "ödendi",
        payment_method: finalPaymentMethod,
        discount: 0
      });
      console.log("✅ Ödenen sipariş oluşturuldu:", paidOrderResponse.data.id);

      // ESKİ SİPARİŞİ GÜNCELLE VEYA SİL
      const currentOrderId = selectedTable.current_order_id;

      if (currentOrderId) {
        if (remainingItems.length > 0) {
          // Kalan ürünler var - güncelle
          const remainingTotal = remainingItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          
          await axios.put(`${API}/orders/${currentOrderId}`, {
            items: remainingItems,
            total: remainingTotal,
            payment_status: "ödenmedi"
          });
          console.log(`✅ Kalan sipariş güncellendi: ${remainingTotal} TL`);
          
          setCart([]);
          setSelectedTable({
            ...selectedTable,
            order_items: remainingItems,
            order_total: remainingTotal
          });
        } else {
          // Tüm ürünler ödendi - sil
          await axios.delete(`${API}/orders/${currentOrderId}`);
          await axios.put(`${API}/tables/${selectedTable.id}`, {
            status: "boş",
            current_order_id: null
          });
          console.log("✅ Tüm ürünler ödendi, masa boşaltıldı");
          
          setCart([]);
          setShowPartialPaymentModal(false);
          setShowOrderModal(false);
          setSelectedTable(null);
        }
      }

      setShowPartialPaymentModal(false);
      setSelectedItemsForPayment([]);
      setPaymentMethod("nakit");
      setMealCardType("sodexo");
      loadTables();
      
      console.log("🔥 ========== KISMİ ÖDEME TAMAMLANDI ==========");
      
    } catch (error) {
      console.error("❌ Kısmi ödeme hatası:", error);
      alert(`Kısmi ödeme başarısız!\n\nHata: ${error.response?.data?.detail || error.message}`);
    }
  };


  const handleCancelOrder = async () => {
    console.log("handleCancelOrder çağrıldı, selectedTable:", selectedTable);
    
    const cancelReason = prompt("İptal nedeni giriniz:\n(Bu alan zorunludur)");
    
    if (!cancelReason || cancelReason.trim() === "") {
      alert("İptal nedeni girmeniz gerekiyor");
      return;
    }

    if (!window.confirm(`Siparişi iptal etmek istediğinize emin misiniz?\n\nİptal Nedeni: ${cancelReason}\n\nBu işlem geri alınamaz.`)) {
      console.log("Kullanıcı iptal işlemini iptal etti");
      return;
    }

    try {
      console.log("Sipariş iptal işlemi başlıyor...");
      
      // Get all orders for this table
      const ordersResponse = await axios.get(`${API}/orders`);
      console.log("Tüm siparişler:", ordersResponse.data);
      
      const tableOrders = ordersResponse.data.filter(
        o => o.table_id === selectedTable.id && o.payment_status === "ödenmedi"
      );
      console.log("Masa siparişleri:", tableOrders);

      // Cancel all unpaid orders with reason
      for (const order of tableOrders) {
        console.log("Sipariş iptal ediliyor:", order.id);
        await axios.put(`${API}/orders/${order.id}`, {
          payment_status: "iptal",
          special_note: `İptal Nedeni: ${cancelReason}`
        });
      }

      // Clear the table
      console.log("Masa boşaltılıyor:", selectedTable.id);
      await axios.put(`${API}/tables/${selectedTable.id}`, {
        status: "boş",
        current_order_id: null
      });
      
      alert("Sipariş iptal edildi");
      
      setShowOrderModal(false);
      setCart([]);
      setSelectedTable(null);
      setDiscount(0);
      
      loadTables();
    } catch (error) {
      console.error("Sipariş iptal hatası:", error);
      console.error("Error response:", error.response);
      alert("Hata oluştu: " + (error.response?.data?.detail || error.message));
    }
  };

  // Masa taşıma fonksiyonları
  const openTransferModal = () => {
    setShowTransferModal(true);
    setTargetTableId(null);
  };

  const transferTable = async () => {
    if (!targetTableId) {
      alert("Lütfen hedef masa seçin!");
      return;
    }

    if (targetTableId === selectedTable.id) {
      alert("Aynı masaya taşıma yapamazsınız!");
      return;
    }

    try {
      const targetTable = tables.find(t => t.id === targetTableId);
      
      if (targetTable.status === "dolu") {
        alert("Hedef masa dolu! Lütfen boş bir masa seçin.");
        return;
      }

      // Siparişi güncelle - table_id ve table_number değiştir
      await axios.put(`${API}/orders/${selectedTable.current_order_id}`, {
        table_id: targetTableId,
        table_number: targetTable.number
      });

      // Eski masayı boşalt
      await axios.put(`${API}/tables/${selectedTable.id}`, {
        status: "boş",
        current_order_id: null
      });

      // Yeni masayı dolu yap
      await axios.put(`${API}/tables/${targetTableId}`, {
        status: "dolu",
        current_order_id: selectedTable.current_order_id
      });

      setShowTransferModal(false);
      setShowOrderModal(false);
      setSelectedTable(null);
      setTargetTableId(null);
      loadTables();
      
      alert(`Sipariş başarıyla ${targetTable.number} masasına taşındı!`);
    } catch (error) {
      console.error("Masa taşıma hatası:", error);
      alert("Masa taşıma başarısız: " + (error.response?.data?.detail || error.message));
    }
  };
  const handlePrintReceipt = () => {
    console.log("🖨️ Yazdırma işlemi başlatılıyor...");
    
    const printContent = document.getElementById('print-content');
    if (!printContent) {
      alert("Yazdırma içeriği bulunamadı");
      return;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Masa Sipariş Fişi</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('body { font-family: monospace; margin: 0; padding: 0; }');
    printWindow.document.write('* { box-sizing: border-box; }');
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    
    // Modal'ı kapat
    handlePrintComplete();
  };

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category_id === selectedCategory)
    : products;

  return (
    <div className="p-8" data-testid="tables-page">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Masa Yönetimi</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {tables.map(table => {
          const duration = getTableDuration(table);
          return (
            <div
              key={table.id}
              onClick={() => handleTableClick(table)}
              className={`${getStatusColor(table.status)} rounded-xl p-6 text-white shadow-lg cursor-pointer hover:shadow-2xl transition hover:scale-105`}
            >
              <div className="flex items-center justify-between mb-4">
                <Grid3x3 size={32} />
                <span className="text-sm font-medium opacity-90">{table.status.toUpperCase()}</span>
              </div>
              <h3 className="text-3xl font-bold mb-2">{table.number}</h3>
              <p className="text-sm opacity-90">{table.capacity} Kişilik</p>
              {duration && (
                <div className="mt-3 pt-3 border-t border-white/30">
                  <p className="text-lg font-bold">⏱️ {duration}</p>
                </div>
              )}
              <div className="mt-4 flex items-center justify-center space-x-2 bg-white bg-opacity-20 rounded-lg py-2">
                <Plus size={20} />
                <span className="text-sm font-medium">
                  {table.status === "boş" ? "Sipariş Ekle" : "Sipariş Görüntüle"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {tables.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <Grid3x3 size={64} className="mx-auto mb-4 opacity-50" />
          <p>Henüz masa eklenmemiş</p>
          <p className="text-sm">Yönetim panelinden masa ekleyebilirsiniz</p>
        </div>
      )}

      {/* Order Modal */}
      {showOrderModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[90vw] h-[90vh] flex overflow-hidden">
            {/* Left: Products */}
            <div className="flex-1 p-6 overflow-auto">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {selectedTable.number} - Sipariş
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedTable.status === "boş" ? "Yeni sipariş oluştur" : "Mevcut siparişe ekle"}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowOrderModal(false);
                    setCart([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Categories */}
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-6">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition ${
                      selectedCategory === cat.id
                        ? "bg-orange-600 text-white shadow-md"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Products */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    onClick={() => addToCart(product)}
                    className="bg-white border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-orange-400 hover:shadow-lg transition"
                  >
                    {product.image_url && (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-24 object-cover rounded-lg mb-3"
                      />
                    )}
                    <h3 className="font-semibold text-gray-800 mb-1 text-sm">{product.name}</h3>
                    <p className="text-orange-600 font-bold">{product.price} ₺</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Cart */}
            <div className="w-[500px] bg-gray-50 p-6 flex flex-col border-l">
              <h3 className="text-xl font-bold mb-4">Sipariş</h3>
              
              <div className="flex-1 overflow-auto mb-4">
                {/* Mevcut siparişler (varsa) */}
                {selectedTable?.order_items && selectedTable.order_items.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-gray-600 mb-2 bg-blue-100 px-2 py-1 rounded">
                      📋 Mevcut Siparişler
                    </div>
                    <div className="space-y-3">
                      {selectedTable.order_items.map((item, index) => (
                        <div key={`existing-${index}`} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm">{item.product_name}</h4>
                              {item.size && (
                                <div className="text-xs text-purple-600 font-medium mt-1">
                                  🍕 {item.size}
                                </div>
                              )}
                              {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                                <div className="text-xs text-red-600 mt-1">
                                  ❌ {item.removed_ingredients.join(", ")}
                                </div>
                              )}
                              {item.added_ingredients && item.added_ingredients.length > 0 && (
                                <div className="text-xs text-green-600 mt-1">
                                  ➕ {item.added_ingredients.join(", ")}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {/* Silme Butonu */}
                              <button
                                onClick={async () => {
                                  if (window.confirm(`"${item.product_name}" ürününü sipariş listesinden çıkarmak istediğinize emin misiniz?`)) {
                                    try {
                                      // Ürünü listeden çıkar
                                      const updatedItems = selectedTable.order_items.filter((_, i) => i !== index);
                                      
                                      if (updatedItems.length > 0) {
                                        // Kalan ürünlerle siparişi güncelle
                                        const newTotal = updatedItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);
                                        await axios.put(`${API}/orders/${selectedTable.current_order_id}`, {
                                          items: updatedItems,
                                          total: newTotal
                                        });
                                      } else {
                                        // Tüm ürünler silindi, siparişi sil
                                        await axios.delete(`${API}/orders/${selectedTable.current_order_id}`);
                                        await axios.put(`${API}/tables/${selectedTable.id}`, {
                                          status: "boş",
                                        current_order_id: null
                                      });
                                    }
                                    
                                    // Tabloyu yenile
                                    loadTables();
                                    
                                    // Modal'ı kapat veya güncelle
                                    if (updatedItems.length === 0) {
                                      setShowOrderModal(false);
                                      setSelectedTable(null);
                                    } else {
                                      // Tabloyu güncelle
                                      const updatedTable = {
                                        ...selectedTable,
                                        order_items: updatedItems,
                                        order_total: updatedItems.reduce((sum, i) => sum + (i.price * i.quantity), 0)
                                      };
                                      setSelectedTable(updatedTable);
                                    }
                                  } catch (error) {
                                    console.error("Ürün silme hatası:", error);
                                    alert("Ürün silinemedi: " + (error.response?.data?.detail || error.message));
                                  }
                                }
                              }}
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Adet: {item.quantity}</span>
                            <span className="font-bold text-blue-600">
                              {(item.price * item.quantity).toFixed(2)} ₺
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Yeni eklenen ürünler */}
                {cart.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-gray-600 mb-2 bg-green-100 px-2 py-1 rounded">
                      ➕ Yeni Eklenecekler
                    </div>
                    <div className="space-y-3">
                      {cart.map((item, index) => (
                        <div key={`${item.product_id}-${index}`} className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm">{item.product_name}</h4>
                              {item.size && (
                                <div className="text-xs text-purple-600 font-medium mt-1">
                                  🍕 {item.size}
                                </div>
                              )}
                              {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                                <div className="text-xs text-red-600 mt-1">
                                  ❌ {item.removed_ingredients.join(", ")}
                                </div>
                              )}
                              {item.added_ingredients && item.added_ingredients.length > 0 && (
                                <div className="text-xs text-green-600 mt-1">
                                  ➕ {item.added_ingredients.join(", ")}
                                </div>
                              )}
                              {item.special_note && (
                                <div className="text-xs text-gray-600 mt-1 italic">
                                  📝 {item.special_note}
                                </div>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  const product = products.find(p => p.id === item.product_id);
                                  if (product) openIngredientModal(product, index);
                                }}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => removeFromCart(item.product_id, index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateQuantity(index, -1)}
                                className="w-7 h-7 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="w-8 text-center font-bold">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(index, 1)}
                                className="w-7 h-7 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                            <span className="font-bold text-orange-600">
                              {(item.price * item.quantity).toFixed(2)} ₺
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hiçbir şey yoksa */}
                {(!selectedTable?.order_items || selectedTable.order_items.length === 0) && cart.length === 0 && (
                  <div className="text-center text-gray-400 py-12">
                    <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Henüz sipariş yok</p>
                  </div>
                )}
              </div>

              <div className="border-t pt-3">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold">Toplam:</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {(() => {
                      // Mevcut siparişler + yeni eklenenler
                      const existingTotal = selectedTable?.order_items 
                        ? selectedTable.order_items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                        : 0;
                      const newTotal = calculateTotal();
                      return (existingTotal + newTotal).toFixed(2);
                    })()}
                     ₺
                  </span>
                </div>
                
                <div className="space-y-2">
                  {/* Sipariş Kaydet - Sadece CART doluysa aktif */}
                  <button
                    onClick={addOrderToTable}
                    disabled={cart.length === 0}
                    className="w-full py-2 text-sm bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Sipariş Kaydet
                  </button>
                  
                  {/* Sipariş Kaydet ve Yazdır - Sadece CART doluysa aktif */}
                  <button
                    onClick={addOrderAndPrint}
                    disabled={cart.length === 0}
                    className="w-full py-2 text-sm bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Sipariş Kaydet ve Yazdır
                  </button>
                  
                  {/* Siparişi Yazdır - Dolu masada MEVCUT siparişi yazdır */}
                  {selectedTable && selectedTable.status === "dolu" && selectedTable.order_items && selectedTable.order_items.length > 0 && (
                    <button
                      onClick={async () => {
                        try {
                          // Mevcut siparişi al ve yazdırma modalını aç
                          const orderResponse = await axios.get(`${API}/orders/${selectedTable.current_order_id}`);
                          const orderWithDetails = {
                            ...orderResponse.data,
                            table_info: `${selectedTable.number}`
                          };
                          setLastOrder(orderWithDetails);
                          setShowPrintModal(true);
                        } catch (error) {
                          console.error("Yazdırma hatası:", error);
                          alert("Sipariş yazdırılamadı: " + (error.response?.data?.detail || error.message));
                        }
                      }}
                      className="w-full py-2 text-sm bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700"
                    >
                      🖨️ Siparişi Yazdır
                    </button>
                  )}
                  
                  {/* Tam Ödeme - Sadece masada MEVCUT sipariş varsa aktif */}
                  <button
                    onClick={initiatePayment}
                    disabled={!selectedTable?.order_items || selectedTable.order_items.length === 0}
                    className="w-full py-2 text-sm bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    💳 Tam Ödeme Yap ve Kapat
                  </button>
                  
                  {/* Kısmi Ödeme - Sadece masada MEVCUT sipariş varsa göster */}
                  {selectedTable && selectedTable.status === "dolu" && selectedTable.order_items && selectedTable.order_items.length > 0 && (
                    <button
                      onClick={openPartialPayment}
                      className="w-full py-2 text-sm bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
                    >
                      🧾 Kısmi Ödeme (Satır Seç)
                    </button>
                  )}
                  
                  {/* Masa Taşıma - Sadece dolu masada göster */}
                  {selectedTable && selectedTable.status === "dolu" && selectedTable.order_items && selectedTable.order_items.length > 0 && (
                    <button
                      onClick={openTransferModal}
                      className="w-full py-2 text-sm bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700"
                    >
                      🔄 Masayı Taşı
                    </button>
                  )}
                  
                  {/* İptal - Sadece dolu masada göster */}
                  {selectedTable && selectedTable.status === "dolu" && (
                    <button
                      onClick={handleCancelOrder}
                      className="w-full py-2 text-sm bg-red-600 text-white rounded-lg font-bold hover:bg-red-700"
                    >
                      ❌ Siparişi İptal Et
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && lastOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg w-[450px] max-h-[90vh] overflow-auto">
            {/* Görünür önizleme */}
            <div className="p-6">
              <div className="text-center border-b-2 border-gray-300 pb-4 mb-4">
                <h2 className="text-2xl font-bold">🍕 VERONA PIZZA</h2>
                <p className="text-sm text-gray-600 mt-2">Masa Sipariş Fişi</p>
                <p className="text-xs text-gray-500 mt-1">{new Date().toLocaleString('tr-TR')}</p>
              </div>

              <div className="mb-4 pb-4 border-b border-gray-200">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">Sipariş No:</span>
                  <span className="text-sm">{lastOrder.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">Masa:</span>
                  <span className="text-sm">{lastOrder.table_info || lastOrder.order_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Müşteri:</span>
                  <span className="text-sm">{lastOrder.customer_name || "Misafir"}</span>
                </div>
              </div>

              <div className="mb-4">
                <h3 className="font-bold mb-3">SİPARİŞ DETAYI:</h3>
                {lastOrder.items.map((item, idx) => (
                  <div key={idx} className="mb-3 pb-3 border-b border-gray-100">
                    <div className="font-semibold">
                      {item.quantity} Adet - {item.product_name}
                      {item.size && <span className="text-gray-600"> ({item.size})</span>}
                    </div>
                    
                    {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                      <div className="text-sm text-red-600 mt-1 ml-2">
                        ❌ Çıkarılacak: {item.removed_ingredients.join(", ")}
                      </div>
                    )}
                    
                    {item.added_ingredients_details && item.added_ingredients_details.length > 0 && (
                      <div className="text-sm text-green-600 mt-1 ml-2">
                        ➕ Ekstra:
                        {item.added_ingredients_details.map((extra, i) => (
                          <div key={i} className="ml-2">
                            • {extra.name} (+{extra.price.toFixed(2)}₺)
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {item.special_note && (
                      <div className="text-sm text-blue-600 mt-1 ml-2 italic">
                        📝 Not: {item.special_note}
                      </div>
                    )}
                    
                    <div className="text-right font-semibold mt-1">
                      {(item.price * item.quantity).toFixed(2)} ₺
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t-2 border-gray-800 pt-4 mb-4">
                <div className="flex justify-between items-center text-xl font-bold">
                  <span>TOPLAM:</span>
                  <span>{lastOrder.total.toFixed(2)} ₺</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Ödeme Durumu: <span className="font-semibold text-orange-600">Bekliyor</span>
                </div>
              </div>

              <div className="text-center text-sm text-gray-500 border-t pt-4">
                <p className="font-bold">🍕 Afiyet olsun! 🍕</p>
              </div>
            </div>

            {/* Gizli yazdırma içeriği */}
            <div id="print-content" style={{ display: 'none' }}>
              <div style={{ 
                fontFamily: 'monospace', 
                width: '300px', 
                margin: '0 auto', 
                padding: '15px',
                fontSize: '12px',
                lineHeight: '1.4'
              }}>
                {/* Header */}
                <div style={{ textAlign: 'center', borderBottom: '2px double black', paddingBottom: '8px', marginBottom: '10px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 4px 0' }}>🍕 VERONA PIZZA</h2>
                  <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '3px 0' }}>MASA SİPARİŞ</p>
                  <p style={{ fontSize: '10px', margin: '2px 0' }}>{new Date().toLocaleString('tr-TR')}</p>
                </div>

                {/* Order Info */}
                <div style={{ marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid black' }}>
                  <p style={{ margin: '3px 0', fontSize: '12px' }}>
                    <b>SİPARİŞ NO:</b> {lastOrder.id.slice(0, 8).toUpperCase()}
                  </p>
                  <p style={{ margin: '3px 0', fontSize: '12px' }}>
                    <b>MASA:</b> {lastOrder.table_info || lastOrder.order_type}
                  </p>
                  <p style={{ margin: '3px 0', fontSize: '12px' }}>
                    <b>MÜŞTERİ:</b> {lastOrder.customer_name || "Misafir"}
                  </p>
                </div>

                {/* Items */}
                <div style={{ marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', textAlign: 'center', borderBottom: '2px solid black', paddingBottom: '4px' }}>
                    SİPARİŞ DETAYI
                  </h3>
                  
                  {lastOrder.items.map((item, idx) => (
                    <div key={idx} style={{ marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px dashed black' }}>
                      {/* Product Name with Adet and Size */}
                      <p style={{ fontSize: '12px', margin: '2px 0', fontWeight: 'bold' }}>
                        {item.quantity} Adet - {item.product_name}
                        {item.size && <span style={{ fontWeight: 'normal' }}> ({item.size})</span>}
                      </p>

                      {/* Removed Ingredients */}
                      {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', lineHeight: '1.3' }}>
                          <b>❌ ÇIKARILACAK:</b> {item.removed_ingredients.join(", ")}
                        </p>
                      )}

                      {/* Added Ingredients */}
                      {item.added_ingredients_details && item.added_ingredients_details.length > 0 && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', lineHeight: '1.3' }}>
                          <b>➕ EKSTRA:</b><br />
                          {item.added_ingredients_details.map((extra, i) => (
                            <span key={i}>
                              &nbsp;&nbsp;• {extra.name} (+{extra.price.toFixed(2)}₺){i < item.added_ingredients_details.length - 1 ? <br /> : ''}
                            </span>
                          ))}
                        </p>
                      )}
                      {!item.added_ingredients_details && item.added_ingredients && item.added_ingredients.length > 0 && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', lineHeight: '1.3' }}>
                          <b>➕ EKSTRA:</b> {item.added_ingredients.join(", ")}
                        </p>
                      )}

                      {/* Special Note */}
                      {item.special_note && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', fontStyle: 'italic', lineHeight: '1.3' }}>
                          <b>📝 NOT:</b> {item.special_note}
                        </p>
                      )}

                      {/* Price */}
                      <p style={{ textAlign: 'right', fontSize: '13px', fontWeight: 'bold', marginTop: '4px' }}>
                        {(item.price * item.quantity).toFixed(2)} ₺
                      </p>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div style={{ borderTop: '3px double black', paddingTop: '8px', marginBottom: '10px' }}>
                  <p style={{ fontSize: '15px', fontWeight: 'bold', margin: '5px 0', textAlign: 'center' }}>
                    TOPLAM: {lastOrder.total.toFixed(2)} ₺
                  </p>
                  <p style={{ fontSize: '12px', marginTop: '5px', textAlign: 'center' }}>
                    <b>ÖDEME DURUMU:</b> ⏳ Bekliyor
                  </p>
                </div>

                {/* Footer */}
                <div style={{ textAlign: 'center', paddingTop: '8px', borderTop: '1px dashed black', fontSize: '11px' }}>
                  <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '5px 0' }}>
                    🍕 AFİYET OLSUN! 🍕
                  </p>
                  <p style={{ fontSize: '10px', margin: '3px 0' }}>
                    Verona Pizza
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex space-x-3 p-4 bg-gray-50 border-t print:hidden">
              <button
                onClick={handlePrintComplete}
                className="flex-1 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
              >
                Kapat
              </button>
              <button
                onClick={handlePrintReceipt}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold"
              >
                🖨️ Yazdır
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ingredient Customization Modal */}
      {showIngredientModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
          <div className="bg-white rounded-lg w-[600px] max-h-[90vh] overflow-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">{selectedProduct.name}</h2>
                  <p className="text-orange-600 font-bold text-lg mt-1">
                    {selectedSize && selectedProduct.sizes && selectedProduct.sizes.length > 0
                      ? `${selectedProduct.sizes.find(s => s.name === selectedSize)?.price || selectedProduct.price} ₺`
                      : `${selectedProduct.price} ₺`
                    }
                  </p>
                  {selectedProduct.description && (
                    <p className="text-sm text-gray-600 mt-2">{selectedProduct.description}</p>
                  )}
                </div>
                <button
                  onClick={closeIngredientModal}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Size Selection */}
              {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-gray-800 mb-3">Boyut Seçin</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedProduct.sizes.map((size, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedSize(size.name)}
                        className={`p-4 rounded-lg border-2 transition text-center ${
                          selectedSize === size.name
                            ? "border-orange-500 bg-orange-50"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div className="font-bold text-lg">{size.name}</div>
                        <div className="text-orange-600 font-semibold">{size.price} ₺</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Default Ingredients - Accordion */}
              {selectedProduct.ingredients && selectedProduct.ingredients.length > 0 && (
                <div className="mb-6 border-2 border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setIsIngredientsOpen(!isIngredientsOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold text-gray-800">Malzeme Çıkar</h3>
                      {removedIngredients.length > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {removedIngredients.length} çıkarıldı
                        </span>
                      )}
                    </div>
                    {isIngredientsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  {isIngredientsOpen && (
                    <div className="p-4 bg-white">
                      <p className="text-sm text-gray-600 mb-3">Çıkarmak istediğiniz malzemeleri seçin:</p>
                      <div className="space-y-2">
                        {selectedProduct.ingredients.map((ingredient, idx) => (
                          <button
                            key={idx}
                            onClick={() => toggleRemovedIngredient(ingredient)}
                            className={`w-full p-3 rounded-lg border-2 transition text-left flex items-center justify-between ${
                              removedIngredients.includes(ingredient)
                                ? "border-red-500 bg-red-50 line-through text-red-600"
                                : "border-gray-200 hover:border-gray-300 bg-white"
                            }`}
                          >
                            <span className="font-medium">{ingredient}</span>
                            {removedIngredients.includes(ingredient) && (
                              <span className="text-red-600 font-bold">❌ Çıkarıldı</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Extra Ingredients - Accordion */}
              {selectedProduct.extra_ids && selectedProduct.extra_ids.length > 0 && (
                <div className="mb-6 border-2 border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setIsExtrasOpen(!isExtrasOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold text-gray-800">Ekstra Malzemeler</h3>
                      {addedIngredients.length > 0 && (
                        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                          {addedIngredients.length} seçili
                        </span>
                      )}
                    </div>
                    {isExtrasOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  {isExtrasOpen && (
                    <div className="p-4 bg-white">
                      <p className="text-sm text-gray-600 mb-3">Eklemek istediğiniz malzemeleri seçin:</p>
                      <div className="space-y-2">
                        {selectedProduct.extra_ids.map((extraId, idx) => {
                          const extraItem = extras.find(e => e.id === extraId);
                          if (!extraItem) return null;
                          
                          // Calculate price based on selected size
                          let priceText = "";
                          if (extraItem.size_prices && selectedSize) {
                            const sizePrice = extraItem.size_prices.find(
                              (sp) => sp.size === selectedSize.toLowerCase()
                            );
                            priceText = sizePrice ? `+${sizePrice.price.toFixed(2)}₺` : `+${extraItem.price.toFixed(2)}₺`;
                          } else {
                            priceText = `+${extraItem.price.toFixed(2)}₺`;
                          }
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => toggleAddedIngredient(extraItem.name)}
                              className={`w-full p-3 rounded-lg border-2 transition text-left flex items-center justify-between ${
                                addedIngredients.includes(extraItem.name)
                                  ? "border-green-500 bg-green-50 text-green-600"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                              }`}
                            >
                              <div>
                                <span className="font-medium">{extraItem.name}</span>
                                <span className="text-sm text-gray-500 ml-2">({priceText})</span>
                              </div>
                              {addedIngredients.includes(extraItem.name) && (
                                <span className="text-green-600 font-bold">➕ Eklendi</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Special Note */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 mb-3">Özel Not</h3>
                <textarea
                  value={specialNote}
                  onChange={(e) => setSpecialNote(e.target.value)}
                  placeholder="Özel taleplerinizi buraya yazabilirsiniz..."
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:outline-none resize-none"
                  rows="3"
                />
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 mb-3">Miktar</h3>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setCustomQuantity(Math.max(1, customQuantity - 1))}
                    className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-300"
                  >
                    <Minus size={20} />
                  </button>
                  <span className="text-2xl font-bold w-16 text-center">{customQuantity}</span>
                  <button
                    onClick={() => setCustomQuantity(customQuantity + 1)}
                    className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-300"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              {/* Total and Add Button */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold">Toplam:</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {(() => {
                      let finalPrice = selectedProduct.price;
                      if (selectedSize && selectedProduct.sizes && selectedProduct.sizes.length > 0) {
                        const sizeInfo = selectedProduct.sizes.find(s => s.name === selectedSize);
                        if (sizeInfo) finalPrice = sizeInfo.price;
                      }
                      
                      // Add extra ingredients prices
                      let extraPrice = 0;
                      addedIngredients.forEach(ingredientName => {
                        if (selectedProduct.extra_ids && selectedProduct.extra_ids.length > 0) {
                          selectedProduct.extra_ids.forEach(extraId => {
                            const extraItem = extras.find(e => e.id === extraId);
                            if (extraItem && extraItem.name === ingredientName) {
                              extraPrice += extraItem.price || 0;
                            }
                          });
                        }
                      });
                      
                      return ((finalPrice + extraPrice) * customQuantity).toFixed(2);
                    })()} ₺
                  </span>
                </div>
                <button
                  onClick={addToCartWithCustomization}
                  className="w-full py-4 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 text-lg"
                >
                  {editingCartIndex !== null ? "Güncelle" : "Sepete Ekle"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-[500px] max-h-[90vh] flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">Ödeme Yöntemi Seçin</h2>
            </div>
            
            {/* Scrollable content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Toplam Tutar */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-semibold text-gray-700">Toplam Tutar:</span>
                  <span className="text-3xl font-bold text-blue-600">
                    {(() => {
                      // Masadaki mevcut siparişlerin toplamını hesapla
                      const orderTotal = selectedTable?.order_items 
                        ? selectedTable.order_items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                        : 0;
                      return orderTotal.toFixed(2);
                    })()}
                     ₺
                  </span>
                </div>
                
                {/* Discount Input */}
                <div className="border-t border-blue-300 pt-3 mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İndirim (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={(() => {
                      const orderTotal = selectedTable?.order_items 
                        ? selectedTable.order_items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                        : 0;
                      return orderTotal;
                    })()}
                    value={discount}
                    onChange={(e) => {
                      const orderTotal = selectedTable?.order_items 
                        ? selectedTable.order_items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                        : 0;
                      setDiscount(Math.max(0, Math.min(orderTotal, parseFloat(e.target.value) || 0)));
                    }}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="İndirim tutarı girin"
                  />
                </div>
                
                {discount > 0 && (
                  <div className="border-t border-blue-300 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-700">Ödenecek Tutar:</span>
                      <span className="text-3xl font-bold text-green-600">
                        {(() => {
                          const orderTotal = selectedTable?.order_items 
                            ? selectedTable.order_items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                            : 0;
                          return (orderTotal - discount).toFixed(2);
                        })()}
                         ₺
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      İndirim: -{discount.toFixed(2)} ₺
                    </p>
                  </div>
                )}
              </div>

              {/* Ödeme Yöntemleri */}
              <div className="space-y-3">
                {/* Nakit */}
                <button
                  onClick={() => setPaymentMethod("nakit")}
                  className={`w-full p-4 rounded-lg border-2 transition flex items-center space-x-4 ${
                    paymentMethod === "nakit"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Banknote size={32} className={paymentMethod === "nakit" ? "text-green-600" : "text-gray-400"} />
                  <div className="flex-1 text-left">
                    <p className="font-bold text-lg">Nakit</p>
                    <p className="text-sm text-gray-600">Peşin ödeme</p>
                  </div>
                  {paymentMethod === "nakit" && (
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  )}
                </button>

                {/* Kredi Kartı */}
                <button
                  onClick={() => setPaymentMethod("kredi-karti")}
                  className={`w-full p-4 rounded-lg border-2 transition flex items-center space-x-4 ${
                    paymentMethod === "kredi-karti"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <CreditCard size={32} className={paymentMethod === "kredi-karti" ? "text-blue-600" : "text-gray-400"} />
                  <div className="flex-1 text-left">
                    <p className="font-bold text-lg">Kredi Kartı</p>
                    <p className="text-sm text-gray-600">Banka kartı ile ödeme</p>
                  </div>
                  {paymentMethod === "kredi-karti" && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  )}
                </button>

                {/* Yemek Kartı */}
                <button
                  onClick={() => setPaymentMethod("yemek-karti")}
                  className={`w-full p-4 rounded-lg border-2 transition flex items-center space-x-4 ${
                    paymentMethod === "yemek-karti"
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <CreditCard size={32} className={paymentMethod === "yemek-karti" ? "text-purple-600" : "text-gray-400"} />
                  <div className="flex-1 text-left">
                    <p className="font-bold text-lg">Yemek Kartı</p>
                    <p className="text-sm text-gray-600">Ticket, Sodexo, vb.</p>
                  </div>
                  {paymentMethod === "yemek-karti" && (
                    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  )}
                </button>

                {/* Yemek Kartı Alt Seçenekleri */}
                {paymentMethod === "yemek-karti" && (
                  <div className="ml-8 space-y-2 mt-3">
                    {["sodexo", "ticket", "multinet", "metropol"].map(type => (
                      <button
                        key={type}
                        onClick={() => setMealCardType(type)}
                        className={`w-full p-3 rounded-lg border transition text-left ${
                          mealCardType === type
                            ? "border-purple-500 bg-purple-100"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <span className="font-medium capitalize">{type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Fixed footer buttons */}
            <div className="p-6 border-t bg-gray-50 flex space-x-4">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
              >
                İptal
              </button>
              <button
                onClick={completeOrder}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold"
              >
                Ödemeyi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kısmi Ödeme Modal */}
      {showPartialPaymentModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[600px] max-h-[80vh] overflow-auto p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              🧾 Kısmi Ödeme - {selectedTable.number}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Ödeme yapılacak ürünleri seçin. Sadece seçilen ürünler için ödeme alınacak.
            </p>

            {/* Ürün Listesi */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-[350px] overflow-auto">
              <h3 className="font-semibold text-gray-700 mb-3">Sipariş İçeriği:</h3>
              {selectedTable.order_items && selectedTable.order_items.length > 0 ? (
                <div className="space-y-3">
                  {selectedTable.order_items.map((item, itemIndex) => {
                    const selection = selectedItemsForPayment.find(s => s.index === itemIndex);
                    const isSelected = !!selection;
                    const selectedQty = selection?.quantity || item.quantity;
                    
                    return (
                      <div
                        key={itemIndex}
                        className={`p-4 rounded-lg border-2 transition ${
                          isSelected
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div
                            onClick={() => toggleItemSelection(itemIndex, item.quantity)}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer flex-shrink-0 mt-1 ${
                              isSelected
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-400 hover:border-blue-400"
                            }`}
                          >
                            {isSelected && (
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          
                          {/* Ürün Bilgileri */}
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-gray-800">
                                  {item.product_name}
                                  {item.size && <span className="text-purple-600 ml-2">({item.size})</span>}
                                </p>
                                {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                                  <p className="text-xs text-red-600">
                                    ❌ Çıkarılan: {item.removed_ingredients.join(", ")}
                                  </p>
                                )}
                                {item.added_ingredients && item.added_ingredients.length > 0 && (
                                  <p className="text-xs text-green-600">
                                    ✅ Eklenen: {item.added_ingredients.join(", ")}
                                  </p>
                                )}
                                {item.special_note && (
                                  <p className="text-xs text-gray-600 italic">
                                    📝 {item.special_note}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600">
                                  Adet: <span className="font-bold">{item.quantity}</span>
                                </p>
                                <p className="font-bold text-orange-600">
                                  {(item.price * item.quantity).toFixed(2)} ₺
                                </p>
                              </div>
                            </div>
                            
                            {/* Miktar Seçimi (Sadece seçili ise ve birden fazla adet varsa göster) */}
                            {isSelected && item.quantity > 1 && (
                              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-blue-200">
                                <span className="text-sm font-medium text-gray-700">Kaç adet ödenecek?</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updatePartialQuantity(itemIndex, selectedQty - 1, item.quantity);
                                    }}
                                    disabled={selectedQty <= 1}
                                    className="w-8 h-8 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                  >
                                    <Minus size={16} className="mx-auto" />
                                  </button>
                                  <span className="w-12 text-center font-bold text-lg text-blue-700">
                                    {selectedQty}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updatePartialQuantity(itemIndex, selectedQty + 1, item.quantity);
                                    }}
                                    disabled={selectedQty >= item.quantity}
                                    className="w-8 h-8 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                  >
                                    <Plus size={16} className="mx-auto" />
                                  </button>
                                </div>
                                <span className="text-sm text-gray-600">
                                  (Max: {item.quantity})
                                </span>
                                <span className="ml-auto text-sm font-bold text-blue-700">
                                  = {(item.price * selectedQty).toFixed(2)} ₺
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Bu masada aktif sipariş yok</p>
              )}
            </div>

            {/* Seçili Toplam */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Seçili Ürünler:</p>
                  <p className="text-lg font-bold text-gray-800">{selectedItemsForPayment.length} adet</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Ödenecek Tutar:</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {calculatePartialTotal().toFixed(2)} ₺
                  </p>
                </div>
              </div>
            </div>

            {/* Ödeme Yöntemleri */}
            {selectedItemsForPayment.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-3">Ödeme Yöntemi Seçin:</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setPaymentMethod("nakit")}
                    className={`w-full p-3 border-2 rounded-lg text-left transition ${
                      paymentMethod === "nakit"
                        ? "border-green-500 bg-green-50"
                        : "border-gray-300 hover:border-green-300"
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        paymentMethod === "nakit" ? "border-green-500" : "border-gray-300"
                      }`}>
                        {paymentMethod === "nakit" && <div className="w-3 h-3 rounded-full bg-green-500"></div>}
                      </div>
                      <span className="font-medium">💵 Nakit</span>
                    </div>
                  </button>

                  <button
                    onClick={() => setPaymentMethod("kredi-karti")}
                    className={`w-full p-3 border-2 rounded-lg text-left transition ${
                      paymentMethod === "kredi-karti"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 hover:border-blue-300"
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        paymentMethod === "kredi-karti" ? "border-blue-500" : "border-gray-300"
                      }`}>
                        {paymentMethod === "kredi-karti" && <div className="w-3 h-3 rounded-full bg-blue-500"></div>}
                      </div>
                      <span className="font-medium">💳 Kredi Kartı</span>
                    </div>
                  </button>

                  <button
                    onClick={() => setPaymentMethod("yemek-karti")}
                    className={`w-full p-3 border-2 rounded-lg text-left transition ${
                      paymentMethod === "yemek-karti"
                        ? "border-orange-500 bg-orange-50"
                        : "border-gray-300 hover:border-orange-300"
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        paymentMethod === "yemek-karti" ? "border-orange-500" : "border-gray-300"
                      }`}>
                        {paymentMethod === "yemek-karti" && <div className="w-3 h-3 rounded-full bg-orange-500"></div>}
                      </div>
                      <span className="font-medium">🍽️ Yemek Kartı</span>
                    </div>
                  </button>
                </div>

                {/* Yemek Kartı Türü */}
                {paymentMethod === "yemek-karti" && (
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Yemek Kartı Türü:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {["sodexo", "ticket", "multinet", "metropol"].map(type => (
                        <button
                          key={type}
                          onClick={() => setMealCardType(type)}
                          className={`p-2 rounded border-2 text-sm font-medium ${
                            mealCardType === type
                              ? "border-orange-500 bg-white text-orange-700"
                              : "border-gray-300 bg-white text-gray-700 hover:border-orange-300"
                          }`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Butonlar */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowPartialPaymentModal(false);
                  setSelectedItemsForPayment([]);
                  setPaymentMethod("nakit");
                  setMealCardType("sodexo");
                }}
                className="flex-1 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
              >
                İptal
              </button>
              <button
                onClick={completePartialPayment}
                disabled={selectedItemsForPayment.length === 0}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                ✓ Kısmi Ödemeyi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Transfer Modal */}
      {showTransferModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90]">
          <div className="bg-white rounded-lg w-[500px] max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Masa Taşıma - {selectedTable.number}
                </h2>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setTargetTableId(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Siparişi taşımak istediğiniz hedef masayı seçin:
                </p>
                
                <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
                  {tables
                    .filter(table => table.id !== selectedTable.id && table.status === "boş")
                    .map(table => (
                      <button
                        key={table.id}
                        onClick={() => setTargetTableId(table.id)}
                        className={`p-4 rounded-lg border-2 transition text-center ${
                          targetTableId === table.id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div className="font-bold text-lg">{table.number}</div>
                        <div className="text-sm text-gray-600">{table.capacity} Kişilik</div>
                        <div className="text-xs text-green-600 mt-1">Boş</div>
                      </button>
                    ))}
                </div>
                
                {tables.filter(table => table.id !== selectedTable.id && table.status === "boş").length === 0 && (
                  <div className="text-center text-gray-400 py-8">
                    <p>Taşıma için uygun boş masa bulunamadı</p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setTargetTableId(null);
                  }}
                  className="flex-1 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
                >
                  İptal
                </button>
                <button
                  onClick={transferTable}
                  disabled={!targetTableId}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  🔄 Masayı Taşı
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablesPage;